import clsx from "clsx";

import type { PresenceEntry } from "../types";

/**
 * Who else is on this ticket right now.
 *
 * "Replying" is deliberately louder than "viewing": two agents reading the same
 * ticket is harmless, two agents writing the same reply is duplicated work and
 * a confused customer who receives two answers.
 */
export function PresenceBar({ entries }: { entries: PresenceEntry[] }) {
  if (!entries.length) return null;

  const replying = entries.filter((entry) => entry.state === "replying");

  return (
    <div className="flex shrink-0 items-center gap-2">
      <div className="flex -space-x-1.5">
        {entries.slice(0, 4).map((entry) => (
          <span
            key={entry.userId}
            title={`${entry.name} is ${entry.state}`}
            className={clsx(
              "relative inline-flex size-6 items-center justify-center rounded-full ring-2",
              entry.state === "replying"
                ? "ring-amber-500"
                : "ring-white dark:ring-slate-900",
            )}
          >
            {entry.avatarUrl ? (
              <img
                src={entry.avatarUrl}
                alt={entry.name}
                className="size-6 rounded-full"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="flex size-6 items-center justify-center rounded-full bg-slate-300 text-[10px] font-medium dark:bg-slate-700">
                {entry.name.slice(0, 1).toUpperCase()}
              </span>
            )}
          </span>
        ))}
      </div>

      {replying.length > 0 ? (
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
          {replying.length === 1
            ? `${replying[0]!.name} is replying…`
            : `${replying.length} agents replying…`}
        </span>
      ) : (
        <span className="text-[11px] text-faint">
          {entries.length === 1 ? "1 other viewing" : `${entries.length} others viewing`}
        </span>
      )}
    </div>
  );
}
